import { ApplicationType } from '@upstart/harmony/lib/constants.js';
import { Deal } from '@upstart/harmony/lib/legos/dealTypes.js';
import { z } from 'zod';

export enum DocumentType {
  ScannedDocument,
  Pack,
  FormTemplate,
}

export const DEFAULT_COPIES = 1;

const driversLicense = z.object({
  front: z.string().url().optional(),
  back: z.string().url().optional(),
  copies: z.number().default(1),
  width: z.number(),
  height: z.number(),
});

export interface SizeOption {
  label: string;
  note: string;
  value: {
    width: number;
    height: number;
  };
}

const transformTemplate =
  (type: DocumentType) =>
  (data: DocumentTemplateData): DocumentTemplate => ({
    ...data,
    copies: DEFAULT_COPIES,
    size: data.sizeOptions?.[0],
    type,
    name: data.name || '',
  });

export const formTemplateSchema = z
  .object({
    id: z.string(),
    name: z.string().nullish(),
    url: z.string().optional(),
  })
  .transform(transformTemplate(DocumentType.FormTemplate));

export type FormTemplate = z.infer<typeof formTemplateSchema>;

export type DriversLicensePrintProps = z.infer<typeof driversLicense>;

const sourceFileSchema = z
  .object({
    copies: z.number().default(1),
    templateId: z.string().optional(),
    url: z.string().url().optional(),
    documentId: z.string().optional(),
  })
  .refine(
    (data) => data.documentId || data.url,
    'document id for document engine, otherwise, url.',
  );

export type MergeSourceFile = z.infer<typeof sourceFileSchema>;

export enum StateKey {
  documents = 'documents',
  dealSummary = 'dealSummary',
  driverLicense = 'driverLicense',
}

export interface DocumentTemplateData {
  id: string;
  name?: string | null;
  applicationType?: ApplicationType;
  requireError?: string;
  attachedFileKey?: string;
  stateKey?: StateKey;
  sizeOptions?: SizeOption[];
  FormTemplates?: FormTemplate[];
  size?: SizeOption;
  documentId?: string;
  requires?: boolean;
  url?: string;
  type?: DocumentType;
}
export interface DocumentTemplate extends DocumentTemplateData {
  copies: number;
  type: DocumentType;
  name: string;
}

export const getScannedDocumentPath = ({
  deal,
  document,
}: {
  deal?: Deal;
  document: DocumentTemplate;
}): { url: string | undefined; pathname: string | undefined } => {
  let url: string | undefined;
  let pathname: string | undefined;

  if (document.stateKey === StateKey.documents) {
    url = deal?.attachedFiles?.documents?.find(
      (doc) => doc.displayName === document.id,
    )?.imageSrc;
  } else {
    url = deal?.attachedFiles?.[
      (document.stateKey ||
        document.attachedFileKey) as keyof typeof deal.attachedFiles
    ] as string;
  }
  if (url) {
    pathname = new URL(url).pathname;
  }
  return { url, pathname };
};

export const getDriversLicenses = ({
  deal,
  copies,
}: {
  deal?: Deal;
  copies: number;
}): DriversLicensePrintProps[] => {
  const licenses: DriversLicensePrintProps[] = [];
  const width = 324; // 324px = 3.375in
  const height = 204; // 204px = 2.125in
  const {
    driverLicense,
    driverLicenseBack,
    altDriverLicense,
    altDriverLicenseBack,
  } = deal?.attachedFiles ?? {};
  if (driverLicense || driverLicenseBack) {
    licenses.push({
      copies,
      front: driverLicense,
      back: driverLicenseBack,
      height,
      width,
    });
  }
  if (altDriverLicense || altDriverLicenseBack) {
    licenses.push({
      copies,
      front: altDriverLicense,
      back: altDriverLicenseBack,
      height,
      width,
    });
  }
  return licenses;
};

export type SavedFormRes = z.input<typeof savedFormResSchema>;

export const savedFormResSchema = z.object({
  url: z.string().optional(),
  documentId: z.string().nullish(),
});

export interface SavedForm extends SavedFormRes {
  id: string;
}

export type SavedFormRecords = Record<string, SavedForm>;
